import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const Row = ({ title, hint, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-5 border-b border-slate-100 last:border-b-0">
    <div><div className="font-medium text-sm">{title}</div><div className="text-xs text-slate-500 mt-0.5">{hint}</div></div>
    {children}
  </div>
);

const SimulateUsage = ({ site }) => {
  const [words, setWords] = useState("41000");
  const run = async () => {
    try {
      const r = await api.post(`/sites/${site.id}/simulate-usage`, { words: Number(words) });
      const row = r.data.sites.find((s) => s.site_id === site.id);
      toast.success(`Now at ${row.used.toLocaleString()} words (${row.pct}%)`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Row title={<span className="flex items-center gap-1.5"><FlaskConical className="h-3.5 w-3.5 text-amber-600" /> Simulate word usage (demo only)</span>} hint="Reports a batch of translated words to test the meter: 41,000 shows amber, 10,000 more shows red and $3.00.">
      <div className="flex gap-2">
        <Input type="number" min="1" value={words} onChange={(e) => setWords(e.target.value)} className="w-32 font-mono" data-testid="simulate-words-input" />
        <Button variant="outline" onClick={run} data-testid="simulate-usage-button">Report</Button>
      </div>
    </Row>
  );
};

export const SettingsTab = ({ site, onChange }) => {
  const { session, config } = useSession();
  const navigate = useNavigate();

  const toggleBanner = async (v) => {
    try {
      await api.patch(`/sites/${site.id}`, { banner_enabled: v });
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/sites/${site.id}`);
      toast.success("Website removed");
      navigate("/");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg max-w-4xl" data-testid="settings-tab">
      <Row title={'"Ver en español" banner'} hint="Shown on English pages to visitors whose browser is set to Spanish.">
        <Switch checked={site.banner_enabled} onCheckedChange={toggleBanner} disabled={!session.can_edit} data-testid="banner-toggle" />
      </Row>
      <Row title="Website key" hint="Ties this website's kit to your business. Keep it in the site's code only.">
        <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded" data-testid="settings-website-key">{site.website_key}</span>
      </Row>
      {config?.demo_mode && session.can_edit && <SimulateUsage site={site} />}
      <Row title="Remove website" hint="Deletes its pages, Spanish text and edits. This can't be undone.">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={!session.can_edit} data-testid="delete-site-button"><Trash2 className="h-4 w-4 mr-1.5" /> Remove</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove {site.domain}?</AlertDialogTitle>
              <AlertDialogDescription>Its Spanish pages stop working right away.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-site-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="delete-site-confirm">Remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Row>
    </div>
  );
};
